import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { avatarFor, processImage, useAvatars } from '../auth/avatars';
import AdminSettings from '../components/AdminSettings';
import ForwardingSettings from '../components/ForwardingSettings';
import IdentitySettings from '../components/IdentitySettings';
import MailShell, { NavButton } from '../components/MailShell';
import { usePanes } from '../ui/viewport';
import type { Language } from '../config/app';
import { useLang, useT } from '../i18n';
import type { Key } from '../i18n/strings';
import { activeHasAdmin, useAccounts } from '../auth/store';
import { ACTIONS, keyLabel, useKeys, type ActionId, type PresetName } from '../keys/store';
import { avatarColor, initials } from '../mail/format';
import { useTheme, type LayoutMode, type ReadingPos, type ThemeMode } from '../theme/store';
import { RADII, THEMES, TOKEN_GROUPS, type Density } from '../theme/tokens';

/* The settings surface. Appearance, Layout, Identity, Keyboard and Accounts are always here;
   Forwarding appears only where the mail server has a ManageSieve service behind it. Rules and
   notifications are not built and say so rather than pretending. */

type Tab = 'appearance' | 'layout' | 'identity' | 'forwarding' | 'domainAdmin' | 'keyboard' | 'accounts';

const card: React.CSSProperties = {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    padding: 18,
    display: 'grid',
    gap: 12,
};

/*
    Rayın simgeleri. Her biri 15px çiziliyor ve hepsi tek çizgi: bir ayar listesinde simge, satırı
    bulmayı hızlandıran bir işaret, kendi başına anlatılacak bir resim değil.
*/
const RAIL_ICONS: Record<Tab, string> = {
    appearance: 'M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.8-.9 1.8-1.7 0-1.6-1.3-1.7-1.3-2.8 0-.8.7-1.3 1.6-1.3h1.6a4.8 4.8 0 0 0 4.8-4.8c0-3.6-3.7-6.4-8.5-6.4z',
    layout: 'M3.5 5.5h17v13h-17zM10 5.5v13',
    identity: 'M4 19a8 8 0 0 1 16 0M12 4.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z',
    forwarding: 'M3.5 7.5h11M14.5 7.5l-3-3M14.5 7.5l-3 3M20.5 16.5h-11M9.5 16.5l3-3M9.5 16.5l3 3',
    keyboard: 'M3.5 6.5h17v11h-17zM7 10h.01M11 10h.01M15 10h.01M8 14h8',
    accounts: 'M4 19.5a8 8 0 0 1 16 0M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8z',
    domainAdmin: 'M12 3.5l7 3v5c0 4-3 7.4-7 8.5-4-1.1-7-4.5-7-8.5v-5z',
};

function Segmented<T extends string | number>(props: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void }) {
    return (
        <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)' }}>
            {props.options.map((o) => {
                const on = o.id === props.value;
                return (
                    <button data-hit
                        key={o.id}
                        onClick={() => props.onChange(o.id)}
                        style={{
                            flex: 1,
                            height: 28,
                            padding: '0 12px',
                            border: 'none',
                            borderRadius: 7,
                            fontSize: 'var(--fs-sm)',
                            fontWeight: on ? 600 : 400,
                            background: on ? 'var(--surface)' : 'transparent',
                            // --text-dim, not --text-mute: muted text on the chip background
                            // measures 4.38:1 on the dark theme and the bar is 4.5. The same
                            // control in the forwarding panel was fixed first; this copy kept
                            // the shortfall because the browser suite had never opened this
                            // screen.
                            color: on ? 'var(--accent-strong)' : 'var(--text-dim)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/*
Rayın bir satırı: simge + ad.

Dar ekranda simge yok ve satırlar yan yana bir şeride diziliyor — orada yer yatay ve bir simge,
adın önünde duran fazladan bir sütun demek.
*/
function RailItem({
    item,
    active,
    onePane,
    onPick,
}: {
    item: { id: Tab; label: string };
    active: boolean;
    onePane: boolean;
    onPick: (id: Tab) => void;
}) {
    return (
        <button
            data-hit
            data-testid={`settings-tab-${item.id}`}
            onClick={() => onPick(item.id)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                width: onePane ? 'auto' : '100%',
                flex: '0 0 auto',
                whiteSpace: 'nowrap',
                padding: '8px 10px',
                border: 'none',
                borderRadius: 'var(--r-btn)',
                textAlign: 'left',
                fontSize: 'var(--fs-md)',
                fontWeight: active ? 600 : 400,
                background: active ? 'var(--accent-bg)' : 'transparent',
                color: active ? 'var(--accent-strong)' : 'var(--text-dim)',
            }}
        >
            {onePane ? null : (
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flex: '0 0 auto' }}
                    aria-hidden="true"
                >
                    <path d={RAIL_ICONS[item.id]} />
                </svg>
            )}
            {/* Taşarsa üç nokta: rayda "Yönlendirme ve otomatik yanıt" 230 piksele sığmıyor ve
                kesildiğinde nerede bittiği belli olmuyordu. Tam adı title'da duruyor. */}
            <span
                title={item.label}
                style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {item.label}
            </span>
        </button>
    );
}

function Appearance() {
    const t = useTheme();
    const { t: s } = useT();
    const lang = useLang((x) => x.lang);
    const setLang = useLang((x) => x.setLang);
    const edited = t.editedKeys();
    const defaults = THEMES[t.resolved];
    const [name, setName] = useState('');
    const [importText, setImportText] = useState('');
    const [importError, setImportError] = useState<string | null>(null);

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <div className="kicker">// {s('settings.language')}</div>
                <Segmented<Language>
                    value={lang}
                    onChange={setLang}
                    options={[{ id: 'tr', label: 'Türkçe' }, { id: 'en', label: 'English' }]}
                />
                <div className="kicker">// {s('settings.baseTheme')}</div>
                <Segmented<ThemeMode>
                    value={t.mode}
                    onChange={t.setMode}
                    options={[
                        { id: 'dark', label: s('settings.dark') },
                        { id: 'light', label: s('settings.light') },
                        { id: 'system', label: s('settings.system') },
                    ]}
                />
                <div className="kicker">// {s('settings.density')}</div>
                <Segmented<Density>
                    value={t.density}
                    onChange={t.setDensity}
                    options={[
                        { id: 'comfortable', label: s('settings.comfortable') },
                        { id: 'cosy', label: s('settings.cosy') },
                        { id: 'compact', label: s('settings.compact') },
                    ]}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="kicker" style={{ width: 84 }}>// {s('settings.baseSize')}</span>
                    <input data-hit type="range" min={11} max={20} step={0.5} value={t.baseSize} onChange={(e) => t.setBaseSize(Number(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)' }} />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', width: 44, textAlign: 'right' }}>{t.baseSize}px</span>
                </label>
                {RADII.map((r) => (
                    <label key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span className="kicker" style={{ width: 84 }}>{r.name}</span>
                        <input data-hit type="range" min={0} max={24} value={t.radii[r.key] ?? r.value} onChange={(e) => t.setRadius(r.key, Number(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)' }} />
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', width: 44, textAlign: 'right' }}>{t.radii[r.key] ?? r.value}px</span>
                    </label>
                ))}
            </div>

            <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="kicker">// {s('settings.tokensLive')}</span>
                    <div style={{ flex: 1 }} />
                    <button data-hit onClick={t.revertTheme} disabled={!edited.length} style={ghost(edited.length > 0)}>{s('settings.revert')}</button>
                </div>
                {TOKEN_GROUPS.map((g) => (
                    <div key={g.title} style={{ display: 'grid', gap: 7 }}>
                        <div className="kicker">// {s(g.title)}</div>
                        {g.keys.map((k) => {
                            const override = t.overrides[t.resolved][k];
                            const value = override ?? defaults[k] ?? '';
                            return (
                                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                    <span style={{ width: 18, height: 18, flex: '0 0 auto', borderRadius: 5, border: '1px solid var(--border2)', background: value }} />
                                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', width: 128, color: 'var(--text-dim)' }}>{k}</span>
                                    <input
                                        data-hit
                                        /* Erişilebilir adı YOKTU — axe'in `label` ihlali burada
                                           tam 13 kere sayılıyordu (dört grup: 4+3+3+3) ve bu ekran
                                           bugüne kadar hiç taranmamıştı. Ad olarak token'ın kendi
                                           adı veriliyor, çünkü ekranda yazan da o: dinleyen kişiyle
                                           bakan kişi aynı şeyi duyuyor. */
                                        aria-label={k}
                                        value={value}
                                        onChange={(e) => t.setToken(k, e.target.value)}
                                        spellCheck={false}
                                        style={{ flex: 1, minWidth: 0, height: 28, padding: '0 10px', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', background: 'var(--surface2)', border: `1px solid ${override ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--r-btn)', outline: 'none' }}
                                    />
                                    {override ? <button data-hit onClick={() => t.clearToken(k)} title={s('settings.resetToken')} style={ghost(true, 26)}>×</button> : <span style={{ width: 26 }} />}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div style={card}>
                <div className="kicker">// {s('settings.savedThemes')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {t.saved.map((saved) => (
                        <div key={saved.name} style={{ width: 186, maxWidth: '100%', padding: 11, borderRadius: 'var(--r-card)', background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                                {['--bg', '--surface', '--accent', '--accent-strong'].map((k) => (
                                    <span key={k} style={{ flex: 1, height: 16, borderRadius: 5, border: '1px solid var(--border)', background: saved.tokens[k] ?? THEMES[saved.base][k] }} />
                                ))}
                            </div>
                            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{saved.name}</div>
                            <div className="kicker" style={{ marginTop: 2 }}>
                                // {s(saved.base === 'dark' ? 'settings.dark' : 'settings.light')} · {s('settings.tokenCount', { count: Object.keys(saved.tokens).length })}
                            </div>
                            {/* Equal thirds that shrink, rather than three
                                natural-width buttons that add up to more than
                                the card and push the last one off the edge. */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5, marginTop: 8 }}>
                                <button data-hit onClick={() => t.applyTheme(saved.name)} style={{ ...ghost(true), padding: '0 4px' }}>{s('settings.apply')}</button>
                                <button data-hit onClick={() => void navigator.clipboard.writeText(t.exportTheme(saved.name))} style={{ ...ghost(true), padding: '0 4px' }}>{s('settings.copy')}</button>
                                <button data-hit onClick={() => t.deleteTheme(saved.name)} style={{ ...ghost(true), padding: '0 4px' }}>{s('settings.delete')}</button>
                            </div>
                        </div>
                    ))}
                    {!t.saved.length ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>{s('settings.noSavedThemes')}</div> : null}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <input data-hit value={name} onChange={(e) => setName(e.target.value)} placeholder={s('settings.nameTheme')} style={{ flex: 1, minWidth: 0, height: 30, padding: '0 10px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)', outline: 'none', fontSize: 'var(--fs-sm)' }} />
                    <button data-hit
                        onClick={() => { t.saveTheme(name); setName(''); }}
                        disabled={!name.trim()}
                        style={{ height: 30, flex: '0 0 auto', padding: '0 14px', background: name.trim() ? 'var(--accent)' : 'var(--chip-bg)', color: name.trim() ? 'var(--on-accent)' : 'var(--text-dim)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: 'var(--fs-sm)', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        {s('settings.saveCurrent')}
                    </button>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <input data-hit value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={s('settings.pasteTheme')} style={{ flex: 1, minWidth: 0, height: 30, padding: '0 10px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)', outline: 'none', fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }} />
                    <button data-hit onClick={() => { setImportError(t.importTheme(importText)); setImportText(''); }} style={{ ...ghost(true), height: 30, flex: '0 0 auto', whiteSpace: 'nowrap' }}>{s('settings.import')}</button>
                </div>
                {importError ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{importError}</div> : null}
            </div>
        </div>
    );
}

function Layout() {
    const t = useTheme();
    const { t: s } = useT();
    const { onePane } = usePanes();
    return (
        <div style={card}>
            {/*
                Gone rather than greyed out on a screen too narrow to hold a
                reading pane at all. On that screen these controls decide
                nothing, and a control that decides nothing is one somebody
                reaches for twice. The stored setting is untouched — widen the
                window and the choice is where it was left.
            */}
            {onePane ? null : (
                <>
            <div className="kicker">// {s('settings.readingPane')}</div>
            <Segmented<ReadingPos>
                value={t.readingPos}
                onChange={t.setReadingPos}
                options={[
                    { id: 'right', label: s('settings.paneRight') },
                    { id: 'bottom', label: s('settings.paneBottom') },
                    { id: 'off', label: s('settings.paneOff') },
                ]}
            />
            {t.readingPos !== 'off' ? (
                <>
                    <div className="kicker">// {s('settings.spaceSplit')}</div>
                    <Segmented<LayoutMode>
                        value={t.layout}
                        onChange={t.setLayout}
                        options={[
                            { id: 'columns', label: s('settings.balanced') },
                            { id: 'wide', label: s('settings.wideReading') },
                        ]}
                    />
                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                        {s('settings.dragHint')}
                    </div>
                </>
            ) : (
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                    {s('settings.paneOffHint')}
                </div>
            )}
                </>
            )}
            {/*
                What this switches is both halves of a conversation, and they are fed from one
                index so they cannot disagree: the READER shows the rest of a conversation when a
                message is opened, replies from Sent included, and the LIST collapses that
                conversation to a single row and counts conversations rather than messages.

                The second half is why this comment was rewritten. It used to end "the list is
                still one row per message", which stopped being true when the list started
                grouping — a comment that quietly describes the opposite of what the code does is
                worse than none, because the next person believes it.
            */}
            <div className="kicker">// {s('settings.conversations')}</div>
            <Segmented<'on' | 'off'>
                value={t.threads ? 'on' : 'off'}
                onChange={(v) => t.setThreads(v === 'on')}
                options={[
                    { id: 'on', label: s('settings.threadsOn') },
                    { id: 'off', label: s('settings.threadsOff') },
                ]}
            />
            <div className="kicker">// {s('settings.undoSend')}</div>
            <Segmented<number>
                value={t.undoSend}
                onChange={t.setUndoSend}
                options={[
                    { id: 0, label: s('settings.undoOff') },
                    { id: 3, label: s('settings.undoSeconds', { count: '3' }) },
                    { id: 5, label: s('settings.undoSeconds', { count: '5' }) },
                    { id: 10, label: s('settings.undoSeconds', { count: '10' }) },
                    { id: 20, label: s('settings.undoSeconds', { count: '20' }) },
                ]}
            />
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>{s('settings.undoHint')}</div>
        </div>
    );
}

function Keyboard() {
    const k = useKeys();
    const { t: s } = useT();
    const map = k.map();
    const conflicts = k.conflicts();
    const [capturing, setCapturing] = useState<ActionId | null>(null);

    useEffect(() => {
        if (!capturing) return;
        const onKey = (e: KeyboardEvent) => {
            e.preventDefault();
            if (e.key === 'Escape') { setCapturing(null); return; }
            if (['ControlLeft', 'ControlRight', 'ShiftLeft', 'ShiftRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight'].includes(e.code)) return;
            k.bind(capturing, e.code);
            setCapturing(null);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [capturing, k]);

    const clashing = new Set(Object.values(conflicts).flat());

    return (
        <div style={card}>
            <div className="kicker">// {s('keys.preset')}</div>
            <Segmented<PresetName>
                value={k.preset}
                onChange={k.setPreset}
                options={[
                    { id: 'thunderbird', label: 'Thunderbird' },
                    { id: 'gmail', label: 'Gmail' },
                    { id: 'vim', label: 'Vim' },
                    { id: 'custom', label: s('keys.custom') },
                ]}
            />
            <div className="kicker">// {s('keys.capture')}</div>

            {Object.keys(conflicts).length ? (
                <div style={{ padding: '9px 11px', background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 'var(--r-btn)', color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>
                    {s('keys.clash')}
                </div>
            ) : null}

            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-btn)', overflow: 'hidden' }}>
                {ACTIONS.map((a) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid var(--border)', background: clashing.has(a.id) ? 'var(--red-bg)' : 'transparent' }}>
                        <span style={{ flex: 1, fontSize: 'var(--fs-sm)' }}>{s(a.label)}</span>
                        <span className="kicker">{s(a.scope)}</span>
                        <button data-hit
                            onClick={() => setCapturing(capturing === a.id ? null : a.id)}
                            style={{
                                minWidth: 46,
                                height: 24,
                                padding: '0 8px',
                                fontFamily: 'var(--font-mono)',
                                fontSize: 'var(--fs-xs)',
                                borderRadius: 6,
                                background: capturing === a.id ? 'var(--accent)' : 'var(--chip-bg)',
                                color: capturing === a.id ? 'var(--on-accent)' : clashing.has(a.id) ? 'var(--red)' : 'var(--text)',
                                border: `1px solid ${capturing === a.id ? 'var(--accent)' : clashing.has(a.id) ? 'var(--red)' : 'var(--border2)'}`,
                            }}
                        >
                            {capturing === a.id ? s('keys.pressing') : keyLabel(map[a.id])}
                        </button>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
                <button data-hit onClick={k.reset} style={ghost(true)}>{s('keys.resetPreset')}</button>
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>{s('keys.physicalHint')}</div>
        </div>
    );
}

/* The connection states the account store reports, as translation keys. */
const STATE_KEY: Record<string, Key> = {
    ready: 'accounts.state.ready',
    connecting: 'accounts.state.connecting',
    error: 'accounts.state.error',
    idle: 'accounts.state.idle',
};

function AccountRow({ email, status, onRemove }: { email: string; status: string; onRemove: () => void }) {
    const { t: s } = useT();
    const picture = useAvatars((x) => avatarFor(x.byEmail, email));
    const setPicture = useAvatars((x) => x.set);
    const clearPicture = useAvatars((x) => x.clear);
    const fileRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);

    const pick = async (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        setError(null);
        try {
            setPicture(email, await processImage(file));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    const avatarStyle: React.CSSProperties = {
        width: 40,
        height: 40,
        flex: '0 0 auto',
        borderRadius: 'var(--r-pill)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--fs-md)',
        fontWeight: 700,
        color: '#fff',
        overflow: 'hidden',
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
            <button data-hit
                onClick={() => fileRef.current?.click()}
                title={s('accounts.changePicture')}
                style={{ ...avatarStyle, padding: 0, border: 'none', background: picture ? 'transparent' : avatarColor(email) }}
            >
                {picture ? <img src={picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(email)}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => void pick(e.target.files)} style={{ display: 'none' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--fs-md)' }}>{email}</div>
                <div className="kicker" style={{ marginTop: 2 }}>// {s(STATE_KEY[status] ?? 'accounts.state.idle')}</div>
                {error ? <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)', marginTop: 3 }}>{error}</div> : null}
            </div>
            <button data-hit onClick={() => fileRef.current?.click()} style={ghost(true)}>{s('accounts.picture')}</button>
            {picture ? <button data-hit onClick={() => clearPicture(email)} style={ghost(true)}>{s('accounts.removePicture')}</button> : null}
            <button data-hit onClick={onRemove} style={ghost(true)}>{s('accounts.signOut')}</button>
        </div>
    );
}

function Accounts() {
    const { plural } = useT();
    const { t: s } = useT();
    const accounts = useAccounts((x) => x.accounts);
    const status = useAccounts((x) => x.status);
    const remove = useAccounts((x) => x.remove);
    return (
        <div style={card}>
            <div className="kicker">// {plural(accounts.length, 'accounts.count.one', 'accounts.count.other')}</div>
            {accounts.map((a) => (
                <AccountRow key={a.id} email={a.email} status={status[a.id] ?? 'idle'} onRemove={() => remove(a.id)} />
            ))}
            <Link to="/login" data-hit style={{ height: 32, padding: '0 14px', display: 'inline-flex', alignItems: 'center', justifySelf: 'start', background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: 'var(--r-btn)', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                {s('accounts.add')}
            </Link>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>{s('accounts.pictureHint')}</div>
        </div>
    );
}

function ghost(enabled: boolean, size?: number): React.CSSProperties {
    return {
        height: size ?? 26,
        width: size,
        padding: size ? 0 : '0 11px',
        background: 'transparent',
        border: '1px solid var(--border2)',
        borderRadius: 'var(--r-btn)',
        color: enabled ? 'var(--text-dim)' : 'var(--text-mute)',
        fontSize: 'var(--fs-sm)',
    };
}

export default function Settings() {
    return (
        <MailShell section="settings">
            <SettingsPanel />
        </MailShell>
    );
}

function SettingsPanel() {
    // A deep link decides the tab — 'make a rule from this message' lands here.
    const [params, setParams] = useSearchParams();
    const tab = (params.get('tab') as Tab | null) ?? 'appearance';
    const setTab = (next: Tab) => setParams(next === 'appearance' ? {} : { tab: next }, { replace: true });
    const { t: s } = useT();
    const { onePane } = usePanes();
    const hasForwarding = useAccounts((a) => a.hasForwarding);
    // Answered by the server at sign-in and about THIS mailbox, not about the installation: the
    // same deployment draws this tab for a domain's administrator and not for anybody else.
    const hasAdmin = useAccounts(activeHasAdmin);

    // Absent where the mail server has no ManageSieve service, rather than a tab that opens onto an
    // explanation of why it is empty. The server answers this at sign-in; the page does not guess.
    /*
        Kişisel ayarlar ve YÖNETİCİ ayrı: alan yönetimi kişinin kendi tercihi değil, bir firmanın
        kutularına dokunan bir yetki. Ray bunu bir ayraç ve bir başlıkla söylüyor.

        ⚠️ Teslimin ray listesinde `Düzen` ve `Hesaplar` YOK ve bu bir sadeleştirme olarak
        alınmadı — ölçüldü: Düzen, okuma paneli / konuşma görünümü / geri alma süresinin TEK
        kapısı; Hesaplar ise çıkış yapmanın, profil resminin ve kutu kaldırmanın tek kapısı (üst
        çubuktaki hesap menüsü yalnız kutu değiştiriyor ve kutu ekliyor). İkisini raydan almak,
        yazılmış iki ekranı ulaşılamaz yapardı. Teslim onları kaldırdığını söylemiyor, hiç
        saymamış.
    */
    const NAV: { id: Tab; label: string }[] = [
        { id: 'appearance', label: s('settings.appearance') },
        { id: 'layout', label: s('settings.layout') },
        { id: 'identity', label: s('settings.identity') },
        ...(hasForwarding ? [{ id: 'forwarding' as Tab, label: s('settings.forwarding') }] : []),
        { id: 'keyboard', label: s('settings.keyboard') },
        { id: 'accounts', label: s('settings.accounts') },
    ];
    const ADMIN_NAV: { id: Tab; label: string }[] = hasAdmin
        ? [{ id: 'domainAdmin', label: s('settings.domainAdmin') }]
        : [];

    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: onePane ? 'column' : 'row',
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {/*
                230px sol ray. Ayarlar artık `Pane`i kullanmıyor: o bileşen rehber ve takvimle
                paylaşılıyor ve genişliği kullanıcının liste genişliği ayarına bağlı — bir ayar
                listesinin genişliği ise posta listesininkiyle aynı şeye bağlı olmamalı.

                Dar ekranda ray yine üstte bir şeride dönüşüyor; o davranış posta ekranınınkiyle
                aynı kalsın diye korundu.
            */}
            <nav
                data-testid="settings-rail"
                style={{
                    display: 'flex',
                    background: 'var(--bg)',
                    minWidth: 0,
                    ...(onePane
                        ? {
                              width: '100%',
                              flex: '0 0 auto',
                              flexDirection: 'row',
                              gap: 4,
                              overflowX: 'auto',
                              padding: 8,
                              borderBottom: '1px solid var(--border)',
                              alignItems: 'center',
                          }
                        : {
                              width: 230,
                              flex: '0 0 auto',
                              flexDirection: 'column',
                              gap: 2,
                              padding: 10,
                              borderRight: '1px solid var(--border)',
                              overflowY: 'auto',
                          }),
                }}
            >
                {onePane ? <NavButton /> : null}
                {NAV.map((n) => (
                    <RailItem key={n.id} item={n} active={tab === n.id} onePane={onePane} onPick={setTab} />
                ))}
                {ADMIN_NAV.length ? (
                    <>
                        {/*
                            Ayraç ve başlık yalnız geniş ekranda: dar ekranda ray yatay bir şerit
                            ve orada bir başlık, ayırdığı şeyin yanında değil önünde durur.
                        */}
                        {onePane ? null : (
                            <>
                                <div style={{ height: 1, background: 'var(--border)', margin: '9px 4px 7px' }} />
                                <div
                                    style={{
                                        fontSize: 'var(--fs-2xs)',
                                        fontWeight: 600,
                                        letterSpacing: '0.6px',
                                        textTransform: 'uppercase',
                                        color: 'var(--text-mute)',
                                        padding: '0 10px 4px',
                                    }}
                                >
                                    {s('settings.adminGroup')}
                                </div>
                            </>
                        )}
                        {ADMIN_NAV.map((n) => (
                            <RailItem key={n.id} item={n} active={tab === n.id} onePane={onePane} onPick={setTab} />
                        ))}
                    </>
                ) : null}
            </nav>

            {/* İçerik sütunu 680px: `Detail` 780 ve o da rehber/takvimle paylaşılıyor. */}
            <section style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', background: 'var(--surface)' }}>
                <div style={{ maxWidth: 680, margin: '0 auto', padding: '18px 22px 60px', display: 'grid', gap: 16 }}>
                {tab === 'appearance' ? <Appearance /> : null}
                {tab === 'layout' ? <Layout /> : null}
                {tab === 'identity' ? <IdentitySettings /> : null}
                {/* The flag is checked here as well as in the tab strip: hiding the tab is not the
                    same as closing the door, and ?tab=forwarding is a URL anybody can type. */}
                {tab === 'forwarding' && hasForwarding ? <ForwardingSettings /> : null}
                {/* The flag is checked HERE too, not only in the tab list: the tab comes from the
                    URL, so `?tab=domainAdmin` would otherwise draw a screen nobody was offered. */}
                {tab === 'domainAdmin' && hasAdmin ? <AdminSettings /> : null}
                {tab === 'keyboard' ? <Keyboard /> : null}
                {tab === 'accounts' ? <Accounts /> : null}
                </div>
            </section>
        </div>
    );
}
